// ═══════════════════════════════════════════════════════════════
// inspection-template — checklist definitions + the blank
// inspection they produce.
//
// A checklist is a named, ORDERED list of sections; each section
// holds items the tech answers Green / Yellow / Red. One section may
// instead be the tire grid (`kind: 'tires'`), which collects per-
// corner tread depth and pressure rather than a single status.
//
// The shipped default mirrors the shop inspection sheet Brad's techs
// already work from (Form #103): Interior/Exterior, Underhood,
// Battery, Under Vehicle, Tires. It follows that sheet's coverage and
// its three-color scale, but is laid out for a phone rather than for
// a clipboard.
//
// Mechanics can edit these and add their own in Settings, so the
// paper form's "Other ______" blank rows are deliberately absent —
// an editable template beats a placeholder you can't label.
// ═══════════════════════════════════════════════════════════════
import { shortId } from './job-helpers.js';

// Status values are STABLE STRINGS — inspections already saved under
// the old 9-item checklist use these same three, so changing them
// would orphan existing data. Only the labels moved to match the
// sheet's wording.
export const INSPECTION_STATUSES = [
  { value: 'good',      label: 'Checked and OK',              short: 'OK' },
  { value: 'attention', label: 'May require attention',       short: 'Attention' },
  { value: 'replace',   label: 'Requires immediate attention', short: 'Immediate' },
];

export const TIRE_CORNERS = [
  { key: 'lf', label: 'Left front',  short: 'LF' },
  { key: 'rf', label: 'Right front', short: 'RF' },
  { key: 'lr', label: 'Left rear',   short: 'LR' },
  { key: 'rr', label: 'Right rear',  short: 'RR' },
];

// The sheet's tire-check suggestions — plain checkboxes.
export const TIRE_SUGGESTIONS = [
  { key: 'alignment', label: 'Alignment' },
  { key: 'balance',   label: 'Balance' },
  { key: 'rotation',  label: 'Rotation' },
  { key: 'newTire',   label: 'New tire' },
];

// Tread-depth colour thresholds, straight off the sheet's legend:
//   7/32" or greater → green
//   3/32" to 6/32"   → yellow
//   2/32" or less    → red
export const TREAD_GOOD_MIN = 7;
export const TREAD_ATTENTION_MIN = 3;

// Coerce a typed measurement to a number, or null if it wasn't
// entered.
//
// Careful: Number('') and Number(null) are both 0, and 0 is finite.
// A bare Number.isFinite() check would read an EMPTY tread field as
// 0/32" and colour that tire red — telling a customer their tire is
// bald when the tech simply never measured it. Reject the empty
// values before coercing.
export function toMeasurement(value) {
  if (value === null || value === undefined) return null;
  // Whitespace coerces to 0 as well, so trim before testing for empty.
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function treadStatus(thirtySeconds) {
  const n = toMeasurement(thirtySeconds);
  if (n === null) return null;
  if (n >= TREAD_GOOD_MIN) return 'good';
  if (n >= TREAD_ATTENTION_MIN) return 'attention';
  return 'replace';
}

export const DEFAULT_CHECKLIST_ID = 'standard-inspection';

// The shipped checklist. Cloned (never mutated) whenever a mechanic
// starts an inspection or edits a copy in Settings.
export const DEFAULT_CHECKLIST = {
  id: DEFAULT_CHECKLIST_ID,
  name: 'Vehicle inspection',
  isDefault: true,
  sections: [
    {
      key: 'interior-exterior',
      label: 'Interior / Exterior',
      items: [
        { key: 'exterior-body',   label: 'Exterior body' },
        { key: 'windshield',      label: 'Windshield / glass' },
        { key: 'wipers',          label: 'Wipers' },
        { key: 'lights',          label: 'Lights (head, brake, turn)' },
        { key: 'interior-lights', label: 'Interior lights' },
        { key: 'ac',              label: 'A/C operation' },
        { key: 'heating',         label: 'Heating' },
      ],
    },
    {
      key: 'underhood',
      label: 'Underhood',
      items: [
        { key: 'engine-oil',      label: 'Engine oil' },
        { key: 'brake-fluid',     label: 'Brake fluid' },
        { key: 'power-steering',  label: 'Power steering fluid' },
        { key: 'washer-fluid',    label: 'Washer fluid' },
        { key: 'belts-hoses',     label: 'Belts & hoses' },
        { key: 'coolant',         label: 'Antifreeze / coolant' },
        { key: 'air-filter',      label: 'Air filter' },
        { key: 'cabin-filter',    label: 'Cabin filter' },
        { key: 'fuel-filter',     label: 'Fuel filter' },
        { key: 'spark-plugs',     label: 'Spark plugs / wires' },
      ],
    },
    {
      key: 'battery',
      label: 'Battery',
      items: [
        { key: 'battery-charge',    label: 'Battery charge' },
        { key: 'battery-condition', label: 'Battery condition' },
        { key: 'battery-cables',    label: 'Cables & connections' },
      ],
    },
    {
      key: 'under-vehicle',
      label: 'Under vehicle',
      items: [
        { key: 'brakes',       label: 'Brakes (pads / shoes)' },
        { key: 'brake-lines',  label: 'Brake lines / hoses' },
        { key: 'steering',     label: 'Steering system' },
        { key: 'shocks',       label: 'Shocks & struts' },
        { key: 'driveline',    label: 'Driveline (axles / CV shaft)' },
        { key: 'exhaust',      label: 'Exhaust system' },
        { key: 'fuel-lines',   label: 'Fuel lines & hoses' },
      ],
    },
    {
      key: 'tires',
      label: 'Tires',
      kind: 'tires',
      items: [],
    },
  ],
};

// ─── Normalizing ────────────────────────────────────────────────
// Templates round-trip through Firestore and through a hand-editable
// Settings screen, so never trust the shape coming back.
export function normalizeChecklist(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const sections = Array.isArray(raw.sections) ? raw.sections : [];
  return {
    id: raw.id || shortId(),
    name: (raw.name || '').trim() || 'Untitled checklist',
    isDefault: !!raw.isDefault,
    sections: sections
      .map((s) => ({
        key: s.key || shortId(),
        label: (s.label || '').trim() || 'Untitled section',
        kind: s.kind === 'tires' ? 'tires' : 'status',
        items: (Array.isArray(s.items) ? s.items : [])
          .map((i) => ({
            key: i.key || shortId(),
            label: (i.label || '').trim(),
          }))
          .filter((i) => i.label),
      }))
      // A status section with no items is dead weight in the wizard;
      // the tire grid legitimately carries no items of its own.
      .filter((s) => s.kind === 'tires' || s.items.length > 0),
  };
}

export function cloneDefaultChecklist() {
  return normalizeChecklist(JSON.parse(JSON.stringify(DEFAULT_CHECKLIST)));
}

export function blankChecklist(name = 'New checklist') {
  return {
    id: shortId(),
    name,
    isDefault: false,
    sections: [
      {
        key: shortId(),
        label: 'Section 1',
        kind: 'status',
        items: [{ key: shortId(), label: 'First item' }],
      },
    ],
  };
}

// A recommendation the tech adds that isn't on the checklist — the
// noise the customer mentioned, a part the template doesn't cover.
// Same shape as a checklist item plus an editable label, so it flows
// through findings and onto the customer's report identically.
export function blankExtraItem() {
  return {
    key: shortId(),
    label: '',
    // Added because something needs attention — that's the whole
    // reason a tech reaches for this — so don't start it blank.
    status: 'attention',
    note: '',
    photos: [],
  };
}

export function blankTireGrid() {
  const corners = {};
  TIRE_CORNERS.forEach(({ key }) => {
    corners[key] = {
      tread: '',
      pressureBefore: '',
      pressureAfter: '',
      wear: null,      // good | attention | replace
    };
  });
  return {
    corners,
    tpms: null,        // good | attention | replace
    suggestions: {},   // { alignment: true, ... }
    note: '',
    photos: [],
  };
}

// Build the empty response document for a checklist. Mirrors the
// template's section/item structure so the wizard can walk it
// directly, and stores the labels alongside the answers — a template
// edited later must never rewrite the history of an inspection that
// already went out to a customer.
export function blankInspectionFromChecklist(checklist) {
  const tpl = normalizeChecklist(checklist) || cloneDefaultChecklist();
  return {
    templateId: tpl.id,
    templateName: tpl.name,
    sections: tpl.sections.map((s) => ({
      key: s.key,
      label: s.label,
      kind: s.kind,
      ...(s.kind === 'tires'
        ? { tires: blankTireGrid() }
        : {
            items: s.items.map((i) => ({
              key: i.key,
              label: i.label,
              status: null,
              note: '',
              photos: [],
            })),
          }),
    })),
    extraItems: [],
    odometer: '',
    comments: '',
    completedAt: null,
  };
}
